import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaTrash, FaTimes, FaDownload, FaImage, FaEdit } from 'react-icons/fa';
import { uploadFile, getFile, deleteFile } from '../../api/files';
import { useBaseComponentFullscreen } from './BaseComponent';

export default function MoodboardComponent({ component, onUpdate, refreshSidebar }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || { items: [] };
  const [inputText, setInputText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [editText, setEditText] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const fileInputRef = useRef(null);
  const gridRef = useRef(null);

  // Smart grid layout algorithm - masonry style with proper overlap prevention
  const generateGridLayout = () => {
    const items = data.items || [];
    if (items.length === 0) return [];
    
    // Get actual container width from the ref
    const containerElement = gridRef.current;
    if (!containerElement) return []; // Don't layout if container not ready
    
    const containerWidth = containerElement.clientWidth;
    const padding = 24; // 12px on each side
    const availableWidth = containerWidth - padding;
    const gap = 12;
    
    // Calculate visual density based on estimated item heights and content
    // This gives a more accurate representation of visual weight
    let totalVisualWeight = 0;
    
    items.forEach(item => {
      if (item.type === 'image') {
        // Calculate estimated image height based on aspect ratio
        const aspectRatio = imageAspectRatios[item.value] || 0.75; // height/width
        const estimatedWidth = Math.min(300, availableWidth / 3); // Rough estimate
        const estimatedHeight = estimatedWidth * aspectRatio;
        
        // Visual weight based on height - taller images count more
        const heightWeight = Math.max(0.5, Math.min(2.5, estimatedHeight / 150)); // 0.5x to 2.5x weight
        totalVisualWeight += heightWeight;
      } else {
        // Text weight based on content length and estimated height
        const textLength = item.value.length;
        const estimatedLines = Math.ceil(textLength / 20); // Rough chars per line
        const estimatedHeight = Math.max(50, estimatedLines * 20 + 30);
        
        // Visual weight based on text height and content
        const heightWeight = Math.max(0.3, Math.min(1.5, estimatedHeight / 80)); // 0.3x to 1.5x weight
        totalVisualWeight += heightWeight;
      }
    });
    
    const itemCount = totalVisualWeight;
    let maxItemWidth, minItemWidth, idealColumnDivisor;
    
    // Base sizing on available space - be more generous when there's plenty of room
    const spacePerItem = availableWidth / Math.max(1, itemCount);
    const hasAmpleSpace = availableWidth > 800; // Generous threshold for ample space
    const hasGoodSpace = availableWidth > 500;   // Moderate threshold
    
    // Scale up items when there are fewer of them AND when there's space available
    if (itemCount <= 3) {
      maxItemWidth = hasAmpleSpace ? 450 : hasGoodSpace ? 380 : 300;
      minItemWidth = hasAmpleSpace ? 250 : hasGoodSpace ? 200 : 150;
      idealColumnDivisor = hasAmpleSpace ? 1.8 : 2.2;
    } else if (itemCount <= 6) {
      maxItemWidth = hasAmpleSpace ? 400 : hasGoodSpace ? 340 : 280;
      minItemWidth = hasAmpleSpace ? 200 : hasGoodSpace ? 160 : 120;
      idealColumnDivisor = hasAmpleSpace ? 2.2 : 2.8;
    } else if (itemCount <= 12) {
      maxItemWidth = hasAmpleSpace ? 350 : hasGoodSpace ? 300 : 240;
      minItemWidth = hasAmpleSpace ? 180 : hasGoodSpace ? 140 : 110;
      idealColumnDivisor = hasAmpleSpace ? 2.8 : 3.2;
    } else {
      maxItemWidth = hasAmpleSpace ? 320 : hasGoodSpace ? 280 : 220;
      minItemWidth = hasAmpleSpace ? 160 : hasGoodSpace ? 120 : 90;
      idealColumnDivisor = hasAmpleSpace ? 3.2 : 3.8;
    }
    
    // Height constraint: try to keep reasonable height for content amount
    const maxReasonableHeight = isFullscreen ? (hasAmpleSpace ? 600 : 500) : (hasAmpleSpace ? 400 : 350);
    const estimatedItemHeight = itemCount <= 3 ? (hasAmpleSpace ? 120 : 100) : 
                               itemCount <= 6 ? (hasAmpleSpace ? 100 : 85) : 
                               (hasAmpleSpace ? 85 : 70);
    
    const idealItemWidth = Math.max(minItemWidth, Math.min(maxItemWidth, availableWidth / idealColumnDivisor));
    
    // Calculate optimal columns to fill the width, with fewer columns for fewer items
    let bestColumns = 1;
    let bestItemWidth = Math.min(availableWidth, maxItemWidth); // Single column fallback, capped at max
    
    // Limit max columns based on item count and available space
    const maxColumnsAllowed = itemCount <= 1.5 ? (hasAmpleSpace ? 2 : 2) : 
                             itemCount <= 3.8 ? (hasAmpleSpace ? 3 : 3) : 
                             itemCount <= 7.5 ? (hasAmpleSpace ? 4 : 4) : 
                             (hasAmpleSpace ? 6 : 5);
    
    // Try different column counts to find the best fit
    const maxPossibleColumns = Math.min(Math.floor(availableWidth / minItemWidth), maxColumnsAllowed);
    
    // Also consider height constraint when choosing columns
    let heightConstrainedColumns = maxPossibleColumns;
    if (itemCount > 0) {
      const estimatedRowsNeeded = Math.ceil(itemCount / maxPossibleColumns);
      const estimatedTotalHeight = estimatedRowsNeeded * estimatedItemHeight;
      
      // If estimated height is too tall, try to use more columns to reduce height
      if (estimatedTotalHeight > maxReasonableHeight && maxPossibleColumns > 1) {
        const targetRows = Math.ceil(maxReasonableHeight / estimatedItemHeight);
        const optimalColumns = Math.min(maxPossibleColumns, Math.ceil(itemCount / targetRows));
        heightConstrainedColumns = Math.max(2, optimalColumns); // At least 2 columns if we have space
      }
    }
    
    for (let cols = 1; cols <= heightConstrainedColumns; cols++) {
      const itemWidth = (availableWidth - (cols - 1) * gap) / cols;
      
      // Accept any width that fits
      if (itemWidth >= minItemWidth) {
        if (itemWidth <= maxItemWidth) {
          bestColumns = cols;
          bestItemWidth = itemWidth;
        } else if (cols === 1) {
          // For single column, allow exceeding max width if needed
          bestColumns = cols;
          bestItemWidth = itemWidth;
        }
      }
    }
    
    // Force at least 2 columns if container is wide enough and we have multiple items
    if (bestColumns === 1 && items.length > 1 && availableWidth >= (minItemWidth * 2 + gap)) {
      bestColumns = 2;
      bestItemWidth = (availableWidth - gap) / 2;
    }
    
    const columns = bestColumns;
    const baseItemWidth = bestItemWidth;
    
    // Track the height of each column
    const columnHeights = new Array(columns).fill(0);
    const layoutItems = [];
    
    items.forEach((item, index) => {
      // Calculate item dimensions first
      let itemWidth = baseItemWidth;
      let itemHeight;
      
      if (item.type === 'image') {
        const aspectRatio = imageAspectRatios[item.value] || 0.75;
        itemHeight = itemWidth * aspectRatio;
        
        // Scale max height with item size for better proportions, be much more generous 
        const maxHeight = hasAmpleSpace ? 
                         (itemCount <= 3 ? baseItemWidth * 1.5 : 
                          itemCount <= 6 ? baseItemWidth * 1.4 : 
                          baseItemWidth * 1.3) :
                         (itemCount <= 3 ? baseItemWidth * 1.3 : 
                          itemCount <= 6 ? baseItemWidth * 1.2 : 
                          baseItemWidth * 1.1);
        if (itemHeight > maxHeight) {
          itemHeight = maxHeight;
          itemWidth = itemHeight / aspectRatio;
        }
        
        // Scale min height with item count and available space
        const minHeight = hasAmpleSpace ? 
                         (itemCount <= 3 ? 120 : itemCount <= 6 ? 100 : 80) :
                         (itemCount <= 3 ? 100 : itemCount <= 6 ? 80 : 60);
        if (itemHeight < minHeight) {
          itemHeight = minHeight;
          itemWidth = itemHeight / aspectRatio;
        }
      } else {
        // For text items, we need to calculate height after determining final width
        // Start with base dimensions, we'll recalculate after spanning
        const baseHeight = hasAmpleSpace ? 
                          (itemCount <= 3 ? 80 : itemCount <= 6 ? 70 : 60) :
                          (itemCount <= 3 ? 60 : itemCount <= 6 ? 55 : 50);
        itemHeight = baseHeight; // Temporary, will be recalculated after spanning
      }
      
      // Determine how many columns this item will span - be much more aggressive since order doesn't matter
      let columnsToSpan = 1;
      // Use seeded randomization for consistent layout across renders
      const spanSeed = Math.sin(index * 7.891 + 42) * 0.5 + 0.5; // 0-1 range
      let shouldMakeWider = spanSeed < (hasAmpleSpace ? 0.35 : 0.25) && columns > 1; // Much more aggressive spanning
      
      if (shouldMakeWider) {
        // Calculate maximum possible width - be much more generous since order doesn't matter
        const maxPossibleWidth = Math.min(
          baseItemWidth * (hasAmpleSpace ? 3.0 : 2.5) + gap,
          availableWidth * (hasAmpleSpace ? 0.75 : 0.65)
        );
        
        // Check if we have enough columns available - allow up to 3 column spans
        const maxColumnsAvailable = Math.floor(maxPossibleWidth / (baseItemWidth + gap));
        columnsToSpan = Math.min(maxColumnsAvailable, hasAmpleSpace ? 3 : 2);
        
        if (columnsToSpan > 1) {
          itemWidth = columnsToSpan * baseItemWidth + (columnsToSpan - 1) * gap;
          
          if (item.type === 'image') {
            const aspectRatio = imageAspectRatios[item.value] || 0.75;
            itemHeight = itemWidth * aspectRatio;
            
            const maxHeight = baseItemWidth * (hasAmpleSpace ? 
                                              (itemCount <= 3 ? 1.5 : itemCount <= 6 ? 1.4 : 1.3) :
                                              (itemCount <= 3 ? 1.3 : itemCount <= 6 ? 1.2 : 1.1));
            if (itemHeight > maxHeight) {
              itemHeight = maxHeight;
              itemWidth = itemHeight / aspectRatio;
              // Recalculate columns based on adjusted width
              columnsToSpan = Math.ceil(itemWidth / (baseItemWidth + gap));
            }
          } else {
            const baseHeight = hasAmpleSpace ? 
                              (itemCount <= 3 ? 90 : itemCount <= 6 ? 80 : 70) :
                              (itemCount <= 3 ? 70 : itemCount <= 6 ? 65 : 60);
            const maxHeight = hasAmpleSpace ? 
                             (itemCount <= 3 ? 180 : itemCount <= 6 ? 170 : 160) :
                             (itemCount <= 3 ? 140 : itemCount <= 6 ? 135 : 130);
            // More accurate text height calculation for spanning items
            const textLength = item.value.length;
            // Account for wider width when calculating lines
            const charsPerLine = Math.max(12, Math.floor(itemWidth / 11)); // More space for wider items
            const estimatedLines = Math.ceil(textLength / charsPerLine);
            const calculatedHeight = Math.max(baseHeight, estimatedLines * 20 + 40);
            itemHeight = Math.min(maxHeight, calculatedHeight);
          }
        } else {
          columnsToSpan = 1; // Fall back to single column
        }
      }
      
      // For single-column text items, calculate height based on actual width
      if (item.type === 'text' && columnsToSpan === 1) {
        const baseHeight = hasAmpleSpace ? 
                          (itemCount <= 3 ? 80 : itemCount <= 6 ? 70 : 60) :
                          (itemCount <= 3 ? 60 : itemCount <= 6 ? 55 : 50);
        const maxHeight = hasAmpleSpace ? 
                         (itemCount <= 3 ? 160 : itemCount <= 6 ? 150 : 140) :
                         (itemCount <= 3 ? 120 : itemCount <= 6 ? 115 : 110);
        // Calculate height with actual final width
        const textLength = item.value.length;
        const charsPerLine = Math.max(8, Math.floor(itemWidth / 11)); // Account for padding and borders
        const estimatedLines = Math.ceil(textLength / charsPerLine);
        const calculatedHeight = Math.max(baseHeight, estimatedLines * 20 + 40); // 20px per line + 40px padding
        itemHeight = Math.min(maxHeight, calculatedHeight);
      }
      
      // Find the best starting column that can accommodate this item
      let bestColumn = 0;
      let bestColumnHeight = Infinity;
      
      for (let col = 0; col <= columns - columnsToSpan; col++) {
        // Find the maximum height among the columns this item would span
        let maxHeightInSpan = 0;
        for (let spanCol = col; spanCol < col + columnsToSpan; spanCol++) {
          maxHeightInSpan = Math.max(maxHeightInSpan, columnHeights[spanCol]);
        }
        
        if (maxHeightInSpan < bestColumnHeight) {
          bestColumnHeight = maxHeightInSpan;
          bestColumn = col;
        }
      }
      
      // Calculate position with centering for images that don't fill their allocated width
      const x = bestColumn * (baseItemWidth + gap);
      const y = bestColumnHeight;
      
      // Center the item horizontally if it's narrower than the base width
      let adjustedX = x;
      if (itemWidth < baseItemWidth) {
        adjustedX = x + (baseItemWidth - itemWidth) / 2;
      }
      
      // Add consistent randomness for less neat appearance (seeded by index for consistency)
      const positionOffset = hasAmpleSpace ? 8 : 6; // Slightly more randomness with ample space
      const rotationOffset = 3; // ±1.5 degrees rotation for dramatic visible tilts
      
      // Use item index as seed for consistent randomization
      const seedX = Math.sin(index * 12.345) * 0.5 + 0.5; // 0-1 range
      const seedY = Math.sin(index * 23.456) * 0.5 + 0.5; // 0-1 range  
      // Better distribution for rotation - use a different multiplier to spread values more evenly
      const seedRotation = (Math.sin(index * 43.21 + 17.89) + 1) * 0.5; // 0-1 range, better distribution
      
      const randomX = adjustedX + (seedX - 0.5) * positionOffset;
      const randomY = y + (seedY - 0.5) * positionOffset;
      const randomRotation = (seedRotation - 0.5) * rotationOffset;
      
      layoutItems.push({
        ...item,
        x: randomX,
        y: randomY,
        width: itemWidth,
        height: itemHeight,
        index,
        columnsSpanned: columnsToSpan,
        rotation: randomRotation
      });
      
      // Update all affected column heights
      for (let spanCol = bestColumn; spanCol < bestColumn + columnsToSpan; spanCol++) {
        columnHeights[spanCol] = y + itemHeight + gap;
      }
    });
    
    return layoutItems;
  };

  const [gridItems, setGridItems] = useState([]);
  const [imageAspectRatios, setImageAspectRatios] = useState({});

  // Update grid layout when items change
  useEffect(() => {
    let resizeTimeout;
    
    const updateLayout = () => {
      if (data.items.length > 0) {
        setGridItems(generateGridLayout());
      } else {
        setGridItems([]);
      }
    };

    // Debounced layout update for better performance
    const debouncedUpdateLayout = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateLayout, 50); // Reduced debounce for more responsiveness
    };

    // Initial layout (immediate)
    updateLayout();

    // Use ResizeObserver to watch container size changes with debouncing
    let resizeObserver;
    if (gridRef.current) {
      resizeObserver = new ResizeObserver(debouncedUpdateLayout);
      resizeObserver.observe(gridRef.current);
    }

    // Fallback window resize listener with debouncing
    const handleResize = debouncedUpdateLayout;
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(resizeTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [data.items, isFullscreen, imageAspectRatios]);

  // Fetch images as blobs and create object URLs, also calculate aspect ratios
  useEffect(() => {
    const fetchImages = async () => {
      const urls = {};
      
      for (const item of data.items.filter(item => item.type === 'image')) {
        try {
          const blob = await getFile(item.value, localStorage.getItem('access_token'));
          const url = URL.createObjectURL(blob);
          urls[item.value] = url;
          
          // Calculate aspect ratio
          const img = new Image();
          img.onload = () => {
            const ratio = img.height / img.width; // height/width for our layout calculations
            setImageAspectRatios(prev => ({ ...prev, [item.value]: ratio }));
          };
          img.src = url;
        } catch (error) {
          console.error(`Failed to load image ${item.value}:`, error);
        }
      }
      
      setImageUrls(urls);
    };
    
    fetchImages();

    return () => {
      Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [data.items]);

  // Handle drag-and-drop events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length > 0) {
      imageFiles.forEach(file => handleFileUpload(file));
    }
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;

    setIsUploading(true);
    try {
      const response = await uploadFile(component.idea_id, file, localStorage.getItem('access_token'));
      const newItem = {
        type: 'image',
        value: response.file_path,
        size: file.size,
        created_at: new Date().toISOString()
      };

      onUpdate({
        ...component,
        data: {
          items: [...data.items, newItem]
        }
      });

      if (refreshSidebar) refreshSidebar();
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
    e.target.value = '';
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await handleFileUpload(file);
        }
        return;
      }
    }
  };

  const handleAddText = () => {
    if (!inputText.trim()) return;

    const newItem = {
      type: 'text',
      value: inputText.trim(),
      created_at: new Date().toISOString()
    };

    onUpdate({
      ...component,
      data: {
        items: [...data.items, newItem]
      }
    });

    setInputText(''); // This will trigger the UI to reset to the default state
  };

  const handleDelete = async (item) => {
    setDeleteConfirm(item);
  };

  const confirmDelete = async () => {
    const item = deleteConfirm;
    setDeleteConfirm(null);

    if (item.type === 'image') {
      try {
        await deleteFile(item.value, localStorage.getItem('access_token'));
        if (imageUrls[item.value]) {
          URL.revokeObjectURL(imageUrls[item.value]);
          setImageUrls(prev => {
            const newUrls = { ...prev };
            delete newUrls[item.value];
            return newUrls;
          });
        }
      } catch (error) {
        alert(`Deletion failed: ${error.message}`);
        return;
      }
    }

    // Find and remove the item by matching its properties (not by reference)
    const newItems = data.items.filter((dataItem, dataIndex) => {
      // For layout items, we need to match by index or by unique properties
      if (item.index !== undefined) {
        return dataIndex !== item.index;
      } else {
        // Fallback: match by type, value, and created_at
        return !(dataItem.type === item.type && 
                dataItem.value === item.value && 
                dataItem.created_at === item.created_at);
      }
    });
    
    onUpdate({
      ...component,
      data: { items: newItems }
    });
    
    if (item.type === 'image' && typeof refreshSidebar === 'function') {
      refreshSidebar();
    }
    setSelectedItem(null);
  };

  const handleEditText = (value) => {
    if (!value.trim()) return;

    const newItems = data.items.map((dataItem, dataIndex) => {
      // Match by index if available, otherwise by properties
      if (selectedItem.index !== undefined) {
        return dataIndex === selectedItem.index ? { ...dataItem, value: value.trim() } : dataItem;
      } else {
        // Fallback: match by type, value, and created_at
        return (dataItem.type === selectedItem.type && 
                dataItem.value === selectedItem.value && 
                dataItem.created_at === selectedItem.created_at) 
               ? { ...dataItem, value: value.trim() } 
               : dataItem;
      }
    });
    
    onUpdate({
      ...component,
      data: { items: newItems }
    });
    
    // Update the selected item to reflect the new value
    setSelectedItem({ ...selectedItem, value: value.trim() });
  };

  const handleDownload = async (item) => {
    try {
      const blob = await getFile(item.value, localStorage.getItem('access_token'));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.value.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Download failed: ${error.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="moodboard-gallery w-full h-full flex flex-col">
      {/* Gallery Grid */}
      <div
        ref={gridRef}
        className={`relative w-full rounded-lg ${
          isDragging ? 'border-2 border-dashed border-[var(--primary)] bg-[var(--bg-hover)]/30' : 'bg-[var(--bg-secondary)]/10'
        } transition-all duration-200 p-2 flex-1 min-h-0 overflow-y-auto`}
        style={{ 
          minHeight: gridItems.length === 0 ? (isFullscreen ? '60vh' : '300px') : 'auto'
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {gridItems.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-[var(--text-secondary)]">
              <FaImage className="mx-auto mb-4 text-4xl opacity-50" />
              <p className="text-lg mb-2">Create your mood gallery</p>
              <p className="text-sm">Add text below or drag & drop images here</p>
            </div>
          </div>
        ) : (
          <div 
            className="relative" 
            style={{ 
              height: `${Math.max(...gridItems.map(item => item.y + item.height)) + 20}px`,
              width: '100%',
              minHeight: gridItems.length <= 3 ? 'auto' : '200px' // Prevent unnecessarily tall layouts for few items
            }}
          >
            {gridItems.map((item, index) => (
              <div
                key={`${item.type}-${item.index}`}
                className="absolute transition-all duration-300 hover:z-10 cursor-pointer group"
                style={{
                  left: `${item.x}px`,
                  top: `${item.y}px`,
                  width: `${item.width}px`,
                  height: `${item.height}px`,
                  transform: `rotate(${item.rotation || 0}deg) scale(1)`,
                  transformOrigin: 'center center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = `rotate(${item.rotation || 0}deg) scale(1.05)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = `rotate(${item.rotation || 0}deg) scale(1)`;
                }}
                onClick={() => {
                  setSelectedItem(item);
                  if (item.type === 'text') {
                    setEditText(item.value);
                  }
                }}
              >
                {item.type === 'image' ? (
                  <div className="relative group w-full h-full">
                    {imageUrls[item.value] ? (
                      <img
                        src={imageUrls[item.value]}
                        alt="Mood item"
                        className="w-full h-full object-cover rounded-lg border-2 border-[var(--bg-hover)] hover:border-[var(--primary)]/50 transition-all mx-auto"
                        style={{ 
                          objectFit: 'cover',
                          objectPosition: 'center',
                          display: 'block' // Ensure block display for centering
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-[var(--bg-hover)] rounded-lg border-2 border-[var(--bg-hover)] flex items-center justify-center">
                        <div className="animate-pulse text-[var(--text-secondary)]">
                          <FaImage className="text-2xl" />
                        </div>
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item);
                      }}
                      className="absolute top-2 right-2 w-7 h-7 backdrop-blur text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center text-sm bg-[var(--bg-hover)] hover:bg-danger hover:scale-105"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="relative group w-full h-full">
                    {editingItem === item.index ? (
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onBlur={() => {
                          if (editText.trim()) {
                            const newItems = [...data.items];
                            newItems[item.index] = { ...newItems[item.index], value: editText.trim() };
                            onUpdate({
                              ...component,
                              data: { items: newItems }
                            });
                          }
                          setEditingItem(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                          if (e.key === 'Escape') {
                            setEditText(item.value);
                            setEditingItem(null);
                          }
                        }}
                        className="w-full h-full px-3 py-2 rounded-lg bg-[var(--bg-primary)] border-2 border-[var(--primary)]/50 shadow-lg text-[var(--text-primary)] font-medium text-center resize-none text-sm"
                        autoFocus
                        maxLength={100}
                      />
                    ) : (
                      <div className="w-full h-full px-3 py-2 rounded-lg bg-[var(--bg-hover)]/20 border-2 border-[var(--bg-hover)] hover:border-[var(--primary)]/50 text-[var(--text-primary)] font-medium flex items-center justify-center text-center transition-all overflow-hidden">
                        <span className="break-words leading-relaxed text-sm px-1 py-1 max-w-full max-h-full overflow-hidden text-center flex items-center justify-center">{item.value}</span>
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item);
                      }}
                      className="absolute top-2 right-2 w-7 h-7 backdrop-blur text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center text-sm bg-[var(--bg-hover)] hover:bg-danger hover:scale-105"
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add new items - Floating interface for fullscreen */}
      {isFullscreen ? (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-[var(--bg-primary)] rounded-lg shadow-2xl px-2 py-2">
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={(e) => e.key === 'Enter' && handleAddText()}
                placeholder="Add a word or phrase..."
                className="w-64 px-4 py-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] transition-all duration-300 placeholder:text-[var(--text-secondary)]/60 text-sm shadow-sm focus:shadow-md"
                maxLength={150}
              />
              
              <button
                onClick={handleAddText}
                disabled={isUploading}
                className="px-6 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-medium flex items-center justify-center text-sm transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <FaPlus className="mr-2 text-xs" />
                Add Text
              </button>
              
              {/* Quick action buttons */}
              <div className="flex gap-2 ml-1 border-l border-[var(--border-color)]/30 pl-2 pr-1">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="p-3 rounded-xl bg-[var(--bg-hover)]/50 hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] hover:text-[var(--primary)] transition-all duration-200 transform hover:scale-110"
                  title="Upload Image"
                >
                  <FaImage className="text-lg" />
                </button>
              </div>
            </div>
            
            {isUploading && (
              <div className="mt-3 text-center text-[var(--text-secondary)] text-sm">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary)] mr-2"></div>
                Uploading image...
              </div>
            )}
          </div>
          
          {/* Hidden file input for fullscreen mode */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUploadImage}
            className="hidden"
            disabled={isUploading}
          />
        </div>
      ) : (
        // Original interface for non-fullscreen mode
        <div className="mt-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={(e) => e.key === 'Enter' && handleAddText()}
              placeholder="Add a word or phrase..."
              className={`${
                inputText.trim() ? 'flex-[3]' : 'flex-1'
              } px-4 py-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] transition-all duration-300 placeholder:text-[var(--text-secondary)]/60 text-sm shadow-sm focus:shadow-md`}
              maxLength={150}
            />
            
            {inputText.trim() ? (
              <button
                onClick={handleAddText}
                disabled={isUploading}
                className="flex-1 px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-medium flex items-center justify-center text-sm transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <FaPlus className="mr-2 text-xs" />
                Add Text
              </button>
            ) : (
              <label className="flex-1 px-4 py-3 bg-[var(--secondary)] text-white rounded-lg hover:bg-[var(--secondary)]/90 cursor-pointer transition-all duration-300 font-medium flex items-center justify-center text-sm transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap">
                <FaImage className="mr-2 text-xs" />
                Add Image
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUploadImage}
                  className="hidden"
                  disabled={isUploading}
                />
              </label>
            )}
          </div>
          
          {isUploading && (
            <div className="mt-4 text-center text-[var(--text-secondary)] text-sm">
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary)] mr-2"></div>
              Uploading image...
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {selectedItem && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-6 rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-[var(--bg-hover)]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg text-[var(--text-primary)] truncate">
                {selectedItem.type === 'image' ? selectedItem.value.split('/').pop() : 'Text Item'}
              </h3>
              <button
                onClick={() => {
                  setSelectedItem(null);
                  setEditText('');
                }}
                className="p-2 text-[var(--text-secondary)] hover:text-[var(--primary)]"
              >
                <FaTimes />
              </button>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                {selectedItem.type === 'image' ? (
                  imageUrls[selectedItem.value] ? (
                    <img
                      src={imageUrls[selectedItem.value]}
                      alt="Moodboard item"
                      className="w-full max-h-[500px] object-contain rounded"
                    />
                  ) : (
                    <div className="w-full h-[500px] bg-[var(--bg-primary)] rounded flex items-center justify-center text-[var(--text-secondary)]">
                      Loading...
                    </div>
                  )
                ) : (
                  <div className="w-full p-4 bg-[var(--bg-secondary)] rounded">
                    <div className="space-y-3">
                      <textarea
                        className="w-full h-32 p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] text-lg resize-none focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] transition-all duration-300"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onBlur={() => handleEditText(editText)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleEditText(editText);
                          }
                        }}
                        placeholder="Enter your text here..."
                        autoFocus
                        maxLength={150}
                      />
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-[var(--text-secondary)]">
                          Press Enter to save
                        </span>
                        <span className={`${
                          editText.length > 120 ? 'text-[var(--warning)]' : 
                          editText.length > 140 ? 'text-[var(--danger)]' : 
                          'text-[var(--text-secondary)]'
                        }`}>
                          {editText.length}/150 characters
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="w-full md:w-64 flex flex-col gap-4">
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Type:</p>
                  <p className="text-[var(--text-primary)] capitalize">{selectedItem.type}</p>
                </div>
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Created:</p>
                  <p className="text-[var(--text-primary)]">{new Date(selectedItem.created_at).toLocaleString()}</p>
                </div>
                {selectedItem.type === 'image' && (
                  <div>
                    <p className="text-sm text-[var(--text-secondary)]">File Size:</p>
                    <p className="text-[var(--text-primary)]">{formatFileSize(selectedItem.size)}</p>
                  </div>
                )}
                {selectedItem.type === 'image' && (
                  <button
                    onClick={() => handleDownload(selectedItem)}
                    className="p-2 bg-[var(--primary)] text-white rounded hover:bg-[var(--primary-dark)] flex items-center gap-2 justify-center"
                  >
                    <FaDownload /> Download Image
                  </button>
                )}
                <button
                  onClick={() => handleDelete(selectedItem)}
                  className="p-2 text-white rounded flex items-center gap-2 justify-center bg-danger hover:bg-danger hover:scale-105 hover:shadow-lg transition-all duration-200"
                >
                  <FaTrash /> Delete Item
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] animate-fade-in">
          <div className="bg-[var(--bg-primary)] p-6 rounded-lg max-w-sm w-full mx-4 shadow-xl border border-[var(--border-color)] animate-scale-in">
            <h3 className="text-lg font-medium mb-4 text-[var(--text-primary)]">Delete Item</h3>
            <p className="mb-6 text-[var(--text-secondary)]">
              Are you sure you want to delete this {deleteConfirm.type === 'image' ? 'image' : 'text'}? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg bg-[var(--bg-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-all duration-200 transform hover:scale-105"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg text-white transition-all duration-200 transform hover:scale-105 bg-danger hover:bg-danger hover:shadow-lg hover:brightness-110"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
